/**
 * MotorcycleVisualNavigator - Motor Gráfico Vetorial & Setas HUD da Honda ADV 150
 * Domínio: Padrão Concessionária Honda Dream & Filosofia Fábio Akita (Deep Modules, Zero Mocks, Fail-Visible)
 */

import {
  ADV150_ANATOMICAL_HOTSPOTS,
  type MotorcycleAnatomyHotspot,
} from "../data/anatomy-hotspots.js";
import { ADV150_CANONICAL_PARTS, type CatalogPartItem } from "../data/catalog.js";

export interface NavigatorOptions {
  containerId: string;
  onHotspotSelect?: (hotspot: MotorcycleAnatomyHotspot, relatedParts: CatalogPartItem[]) => void;
  onPartSelect?: (part: CatalogPartItem, hotspot: MotorcycleAnatomyHotspot | undefined) => void;
  initialXRay?: boolean;
}

export class MotorcycleVisualNavigator {
  private container: HTMLElement | null = null;
  private selectedHotspotId: string | null = null;
  private isXRayActive = false;
  private readonly hotspots = ADV150_ANATOMICAL_HOTSPOTS;
  private readonly parts = ADV150_CANONICAL_PARTS;
  private onHotspotSelectCallback?: (
    hotspot: MotorcycleAnatomyHotspot,
    relatedParts: CatalogPartItem[],
  ) => void;

  constructor(options: NavigatorOptions) {
    this.container = document.getElementById(options.containerId);
    this.onHotspotSelectCallback = options.onHotspotSelect;
    this.isXRayActive = options.initialXRay ?? false;

    if (this.container) {
      this.render();
      this.attachEventListeners();
    }
  }

  public setXRayMode(active: boolean): void {
    this.isXRayActive = active;
    const bodyPanels = this.container?.querySelectorAll(".blueprint-body-panel");
    const internalComponents = this.container?.querySelectorAll(".blueprint-internal");

    if (bodyPanels) {
      for (const el of bodyPanels) {
        (el as HTMLElement).style.opacity = active ? "0.15" : "0.85";
        (el as HTMLElement).style.strokeDasharray = active ? "4 4" : "none";
      }
    }

    if (internalComponents) {
      for (const el of internalComponents) {
        (el as HTMLElement).style.opacity = active ? "1.0" : "0.5";
        (el as HTMLElement).style.filter = active ? "drop-shadow(0 0 6px #00F2FE)" : "none";
      }
    }
  }

  public isXRay(): boolean {
    return this.isXRayActive;
  }

  public selectHotspot(hotspotId: string): void {
    const hotspot = this.hotspots.find((h) => h.id === hotspotId);
    if (!hotspot) return;

    this.selectedHotspotId = hotspotId;
    this.updateHotspotVisuals(hotspotId);
    this.drawCalloutArrow(hotspot);

    const related = this.parts.filter((p) => p.hotspotId === hotspotId);
    if (this.onHotspotSelectCallback) {
      this.onHotspotSelectCallback(hotspot, related);
    }
  }

  public selectPartByPartNumber(partNumber: string): void {
    const part = this.parts.find((p) => p.partNumber.toLowerCase() === partNumber.toLowerCase());
    if (!part) return;

    this.selectHotspot(part.hotspotId);
  }

  public getSelectedHotspot(): MotorcycleAnatomyHotspot | undefined {
    return this.hotspots.find((h) => h.id === this.selectedHotspotId);
  }

  private updateHotspotVisuals(activeId: string): void {
    if (!this.container) return;
    const pins = this.container.querySelectorAll(".hotspot-pin");
    for (const pin of pins) {
      const pinId = pin.getAttribute("data-hotspot-id");
      const isSelected = pinId === activeId;
      pin.classList.toggle("active", isSelected);
      const circle = pin.querySelector(".pin-circle");
      if (circle) {
        circle.setAttribute("r", isSelected ? "10" : "6");
        circle.setAttribute("stroke-width", isSelected ? "3" : "1.5");
      }
    }
  }

  /**
   * Traçado Ortogonal Manhattan com Joelho Suavizado (Callout Arrow)
   * P0(Origem Hotspot) -> P1(Joelho) -> P2(Destino Card/Label)
   */
  private drawCalloutArrow(hotspot: MotorcycleAnatomyHotspot): void {
    if (!this.container) return;
    const arrowLayer = this.container.querySelector("#callout-arrow-layer");
    if (!arrowLayer) return;

    // Converte percentual para viewBox (1200 x 700)
    const p0x = (hotspot.coords.x / 100) * 1200;
    const p0y = (hotspot.coords.y / 100) * 700;

    const p2x = (hotspot.calloutVector.labelAnchorX / 100) * 1200;
    const p2y = (hotspot.calloutVector.labelAnchorY / 100) * 700;

    const p1x = (hotspot.calloutVector.elbowX / 100) * 1200;
    const p1y = (hotspot.calloutVector.elbowY / 100) * 700;

    const color = hotspot.calloutVector.strokeColor;
    const r = 10; // Raio de curvatura do joelho

    // Geometria da curva cúbica suave no joelho
    const pathD = `M ${p2x} ${p2y} L ${p1x} ${p1y} L ${p0x} ${p0y}`;

    arrowLayer.innerHTML = `
      <g class="active-callout-arrow">
        <!-- Linha de sombra / glow HUD -->
        <path d="${pathD}" fill="none" stroke="${color}" stroke-width="5" stroke-opacity="0.3" stroke-linecap="round" stroke-linejoin="round" />
        
        <!-- Linha guia principal pontilhada com animação de traço -->
        <path id="hud-arrow-path" d="${pathD}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" marker-end="url(#marker-arrow-${hotspot.id})" />

        <!-- Círculo de ancoragem no Hotspot -->
        <circle cx="${p0x}" cy="${p0y}" r="4" fill="${color}" />

        <!-- Ponto terminal de apoio -->
        <circle cx="${p2x}" cy="${p2y}" r="5" fill="#0B0F19" stroke="${color}" stroke-width="2" />

        <!-- Balão de chamada HUD na ponta da legenda -->
        <g transform="translate(${p2x - 40}, ${p2y < 350 ? p2y - 28 : p2y + 12})">
          <rect width="80" height="20" rx="4" fill="#0B0F19" stroke="${color}" stroke-width="1.2" opacity="0.95" />
          <text x="40" y="14" font-family="monospace" font-size="9" font-weight="700" fill="${color}" text-anchor="middle">${hotspot.code}</text>
        </g>
      </g>
    `;
  }

  private render(): void {
    if (!this.container) return;

    this.container.innerHTML = `
<div class="motorcycle-blueprint-wrapper" style="position: relative; width: 100%; background: #000000; border: 1px solid #1E293B; border-radius: 12px; overflow: hidden; font-family: system-ui, sans-serif;">
  
  <!-- Header de Controles de Engenharia -->
  <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; background: #070B14; border-bottom: 1px solid #1E293B; flex-wrap: wrap; gap: 8px;">
    <div style="display: flex; align-items: center; gap: 8px;">
      <span style="font-size: 16px;">🛵</span>
      <strong style="color: #F8FAFC; font-size: 13px; letter-spacing: 0.5px;">BLUEPRINT ANATÔMICO HONDA ADV 150 (K0W / KF43E)</strong>
      <span style="background: #1E293B; color: #38BDF8; font-family: monospace; font-size: 10.5px; padding: 2px 6px; border-radius: 4px;">CHASSI KF43</span>
    </div>
    
    <div style="display: flex; gap: 8px; align-items: center;">
      <button type="button" id="btn-toggle-xray" style="min-height: 48px; padding: 8px 14px; background: ${this.isXRayActive ? "#0284C7" : "#1E293B"}; color: #FFF; border: 1px solid #38BDF8; border-radius: 8px; font-weight: 700; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px;">
        <span>☢️</span> Modo Raio-X (Chassi & Motor)
      </button>
      <button type="button" id="btn-reset-view" style="min-height: 48px; padding: 8px 14px; background: #1E293B; color: #94A3B8; border: 1px solid #334155; border-radius: 8px; font-weight: 600; font-size: 12px; cursor: pointer;">
        🔄 Resetar Foco
      </button>
    </div>
  </div>

  <!-- SVG Principal da Moto (ViewBox 1200 x 700) -->
  <div style="position: relative; width: 100%; aspect-ratio: 1200 / 700; background: radial-gradient(circle at 50% 60%, #061124 0%, #000000 75%); overflow: hidden;">
    <svg id="adv150-blueprint-svg" viewBox="0 0 1200 700" style="width: 100%; height: 100%; display: block;" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <!-- Marcadores de Setas HUD -->
        ${this.hotspots
          .map(
            (h) => `
          <marker id="marker-arrow-${h.id}" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="${h.calloutVector.strokeColor}" />
          </marker>
        `,
          )
          .join("")}

        <!-- Padrão de Grade Técnica de Fundo -->
        <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#0F172A" stroke-width="0.8" />
        </pattern>
      </defs>

      <!-- Fundo com Grade Métrica -->
      <rect width="1200" height="700" fill="url(#grid-pattern)" />

      <!-- Linhas de solo e eixo -->
      <line x1="50" y1="570" x2="1150" y2="570" stroke="#1E293B" stroke-width="1.5" stroke-dasharray="6 6" />

      <!-- ============================================== -->
      <!-- SILHUETA MECÂNICA E VETORIAL DA HONDA ADV 150  -->
      <!-- ============================================== -->

      <!-- RODA DIANTEIRA (Aro 14" com Pneu 110/80-14) -->
      <g id="roda-dianteira" transform="translate(220, 480)">
        <!-- Pneu de Uso Misto com Cravos -->
        <circle cx="0" cy="0" r="105" fill="#04070F" stroke="#1E293B" stroke-width="8" />
        <circle cx="0" cy="0" r="88" fill="none" stroke="#334155" stroke-width="2" />
        <!-- Disco Wave Dianteiro 240 mm Nissin -->
        <path d="M -50 -10 Q -60 -25 -45 -45 Q -25 -60 -5 -55 Q 20 -60 40 -45 Q 60 -25 55 -5 Q 60 20 40 40 Q 20 55 -5 50 Q -25 55 -45 40 Z" fill="#0F172A" stroke="#00F2FE" stroke-width="2.2" opacity="0.85" />
        <!-- Anel Pulsador da Roda Fônica ABS -->
        <circle cx="0" cy="0" r="28" fill="none" stroke="#F59E0B" stroke-width="2" stroke-dasharray="2 3" />
        <!-- Cubo e Eixo M12 -->
        <circle cx="0" cy="0" r="16" fill="#1E293B" stroke="#64748B" stroke-width="2" />
      </g>

      <!-- RODA TRASEIRA (Aro 13" com Pneu 130/70-13) -->
      <g id="roda-traseira" transform="translate(850, 495)">
        <!-- Pneu Traseiro -->
        <circle cx="0" cy="0" r="100" fill="#04070F" stroke="#1E293B" stroke-width="10" />
        <circle cx="0" cy="0" r="82" fill="none" stroke="#334155" stroke-width="2" />
        <!-- Disco Wave Traseiro 220 mm -->
        <circle cx="0" cy="0" r="46" fill="#0F172A" stroke="#00F2FE" stroke-width="1.8" opacity="0.8" />
        <!-- Eixo Traseiro com Porca M16 a 118 N.m -->
        <circle cx="0" cy="0" r="18" fill="#1E293B" stroke="#F59E0B" stroke-width="2.5" />
      </g>

      <!-- GARFO DIANTEIRO SHOWA 31mm (130mm Curso) -->
      <g id="suspensao-dianteira">
        <!-- Canelas e Tubos Cromados Showa -->
        <line x1="220" y1="480" x2="330" y2="240" stroke="#64748B" stroke-width="10" stroke-linecap="round" />
        <line x1="228" y1="480" x2="338" y2="240" stroke="#94A3B8" stroke-width="7" stroke-linecap="round" />
        <!-- Mesa Inferior Showa M10 a 27 N.m -->
        <rect x="315" y="235" width="30" height="14" rx="3" fill="#1E293B" stroke="#38BDF8" stroke-width="1.5" />
      </g>

      <!-- BALANÇA OSCILANTE TRASEIRA & REDUTOR -->
      <g id="balanca-traseira">
        <path d="M 580 470 L 850 495 L 830 520 L 590 490 Z" fill="#090E17" stroke="#334155" stroke-width="2" />
      </g>

      <!-- AMORTECEDORES TRASEIROS DUPLOS SHOWA PIGGYBACK -->
      <g id="amortecedores-showa">
        <!-- Amortecedor Traseiro Principal com Molas Duplas -->
        <line x1="810" y1="480" x2="740" y2="330" stroke="#F59E0B" stroke-width="8" stroke-linecap="round" />
        <line x1="810" y1="480" x2="740" y2="330" stroke="#EF4444" stroke-width="4" stroke-dasharray="6 3" />
        <!-- Reservatório Sub-Tanque Piggyback Dourado a Nitrogênio -->
        <rect x="715" y="325" width="18" height="42" rx="4" fill="#D97706" stroke="#FDE047" stroke-width="1.5" />
        <!-- Parafuso Superior M10 (40 N.m) e Inferior M8 (27 N.m) -->
        <circle cx="740" cy="330" r="5" fill="#1E293B" stroke="#F59E0B" stroke-width="2" />
        <circle cx="810" cy="480" r="5" fill="#1E293B" stroke="#F59E0B" stroke-width="2" />
      </g>

      <!-- CHASSI MONOBLOCO BERÇO DUPLO EM TUBOS DE AÇO (Camada Interna / Raio-X) -->
      <g id="chassi-tubos" class="blueprint-internal" opacity="${this.isXRayActive ? "1.0" : "0.5"}">
        <path d="M 330 240 L 410 330 L 520 460 L 600 460 L 760 320 L 880 320" fill="none" stroke="#38BDF8" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" opacity="0.8" />
        <path d="M 410 330 L 460 480 L 580 480 L 740 370" fill="none" stroke="#0284C7" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" opacity="0.7" />
      </g>

      <!-- MOTOR eSP KF43E & TRANSMISSÃO CVT V-MATIC (Internos) -->
      <g id="bloco-motor-cvt" class="blueprint-internal" opacity="${this.isXRayActive ? "1.0" : "0.5"}">
        <!-- Carcaça do CVT -->
        <path d="M 470 450 C 470 420 520 415 620 425 C 720 435 760 440 760 485 C 760 525 710 535 590 525 C 500 515 470 485 470 450 Z" fill="#0B1322" stroke="#00F2FE" stroke-width="2.5" />
        
        <!-- Polia Primária (Virabrequim / Roletes 21g) -->
        <circle cx="510" cy="470" r="42" fill="#040914" stroke="#00F2FE" stroke-width="2" stroke-dasharray="4 2" />
        <!-- Correia Bando Kevlar no Centro -->
        <path d="M 510 445 L 700 440 L 700 495 L 510 495 Z" fill="none" stroke="#EF4444" stroke-width="4" stroke-dasharray="8 3" />
        <!-- Polia Secundária (Campana 125mm / Mola) -->
        <circle cx="700" cy="465" r="46" fill="#040914" stroke="#F59E0B" stroke-width="2" stroke-dasharray="4 2" />
        
        <!-- Cabeçote do Motor e Cilindro Horizontalizado -->
        <rect x="490" y="375" width="75" height="50" rx="6" fill="#0A1628" stroke="#10B981" stroke-width="2" />
        <!-- Radiador Lateral e Bomba d'Água -->
        <rect x="610" y="440" width="38" height="58" rx="4" fill="#0A192F" stroke="#38BDF8" stroke-width="1.8" />
        
        <!-- Bateria Selada YTZ6V sob o Banco -->
        <rect x="585" y="280" width="35" height="28" rx="3" fill="#1C1917" stroke="#F59E0B" stroke-width="1.8" />
      </g>

      <!-- ESCAPAMENTO COM SONDA LAMBDA M12 & CATALISADOR -->
      <g id="escapamento">
        <path d="M 530 425 C 560 480 620 520 740 500 L 890 440 L 890 410 L 730 470 Z" fill="#18181B" stroke="#71717A" stroke-width="2" />
        <!-- Protetor Térmico Preto Fosco da Ponteira -->
        <path d="M 730 480 L 880 430 L 880 415 L 730 465 Z" fill="#27272A" stroke="#52525B" stroke-width="1.2" />
      </g>

      <!-- CARENAGENS PLÁSTICAS EXTERNAS (Silhueta Robusta ADV 150) -->
      <g id="carenagens-externas" class="blueprint-body-panel" opacity="${this.isXRayActive ? "0.15" : "0.85"}" style="transition: opacity 0.3s ease;">
        <!-- Bico Frontal e Carenagem de Farol Full LED -->
        <path d="M 320 220 L 380 180 L 410 240 L 370 290 L 310 270 Z" fill="#7F1D1D" stroke="#EF4444" stroke-width="2" />
        <!-- Para-brisa Ajustável 2 Posições -->
        <path d="M 330 220 L 360 110 L 400 130 L 380 200 Z" fill="#0284C7" stroke="#38BDF8" stroke-width="1.5" opacity="0.35" />
        <!-- Guidão Tapered com Manetes e Reservatórios Nissin -->
        <path d="M 370 190 L 410 170 L 440 210" fill="none" stroke="#E2E8F0" stroke-width="5" stroke-linecap="round" />
        
        <!-- Carenagem Lateral do Túnel e Assoalho -->
        <path d="M 400 250 L 520 280 L 560 410 L 440 410 L 380 320 Z" fill="#991B1B" stroke="#DC2626" stroke-width="2" />
        <!-- Assento Bicolor em Dois Níveis -->
        <path d="M 510 270 C 560 270 630 285 710 290 C 760 295 820 310 820 340 L 530 325 Z" fill="#0F172A" stroke="#334155" stroke-width="2.5" />
        <!-- Carenagem Traseira / Rabeta com Lanterna LED em X -->
        <path d="M 720 320 L 890 320 L 910 340 L 820 380 Z" fill="#7F1D1D" stroke="#EF4444" stroke-width="2" />
        <!-- Paralama Traseiro e Suporte de Placa STW8G80 -->
        <path d="M 870 340 L 930 410 L 920 440 L 860 380 Z" fill="#18181B" stroke="#3F3F46" stroke-width="1.5" />
      </g>

      <!-- CAMADA DE SETAS HUD (Callout Arrows) -->
      <g id="callout-arrow-layer">
        <!-- Renderizado dinamicamente via drawCalloutArrow() -->
      </g>

      <!-- ============================================== -->
      <!-- PINS E HOTSPOTS ANATÔMICOS INTERATIVOS         -->
      <!-- ============================================== -->
      <g id="hotspots-layer">
        ${this.hotspots
          .map((h) => {
            const cx = (h.coords.x / 100) * 1200;
            const cy = (h.coords.y / 100) * 700;
            const isSelected = h.id === this.selectedHotspotId;
            return `
          <g class="hotspot-pin ${isSelected ? "active" : ""}" data-hotspot-id="${h.id}" transform="translate(${cx}, ${cy})" style="cursor: pointer;">
            <!-- Área de toque ergonômica de bancada (>= 48x48dp) -->
            <circle cx="0" cy="0" r="24" fill="transparent" />
            
            <!-- Anel de radar pulsante -->
            <circle class="radar-ripple" cx="0" cy="0" r="14" fill="none" stroke="${h.calloutVector.strokeColor}" stroke-width="1.2" opacity="0.6">
              <animate attributeName="r" values="8;20;8" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0.0;0.8" dur="2.4s" repeatCount="indefinite" />
            </circle>

            <!-- Círculo do Pin Principal -->
            <circle class="pin-circle" cx="0" cy="0" r="${isSelected ? "10" : "6.5"}" fill="#0B0F19" stroke="${h.calloutVector.strokeColor}" stroke-width="${isSelected ? "3" : "1.8"}" />
            <circle cx="0" cy="0" r="2.5" fill="${h.calloutVector.strokeColor}" />
          </g>
        `;
          })
          .join("")}
      </g>
    </svg>
  </div>

  <!-- Barra de Destaque Inferior do Hotspot Selecionado -->
  <div id="hotspot-info-footer" style="padding: 12px 16px; background: #070B14; border-top: 1px solid #1E293B; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
    <div>
      <span style="font-family: monospace; font-size: 11px; color: #94A3B8;">SUBSISTEMA ATIVO:</span>
      <strong id="active-hotspot-title" style="color: #38BDF8; font-size: 13.5px; margin-left: 6px;">Selecione um ponto na moto ou uma peça abaixo</strong>
    </div>
    <div id="active-hotspot-torque" style="font-family: monospace; font-size: 12px; color: #10B981;">
      <!-- Torque oficial -->
    </div>
  </div>

</div>
`;
  }

  private attachEventListeners(): void {
    if (!this.container) return;

    // Hotspot Click
    const pins = this.container.querySelectorAll(".hotspot-pin");
    for (const pin of pins) {
      pin.addEventListener("click", (e) => {
        e.stopPropagation();
        const hotspotId = pin.getAttribute("data-hotspot-id");
        if (hotspotId) {
          this.selectHotspot(hotspotId);
        }
      });
    }

    // Toggle Raio-X
    const btnXRay = this.container.querySelector("#btn-toggle-xray");
    btnXRay?.addEventListener("click", () => {
      this.setXRayMode(!this.isXRayActive);
      if (btnXRay) {
        (btnXRay as HTMLElement).style.background = this.isXRayActive ? "#0284C7" : "#1E293B";
      }
    });

    // Reset View
    const btnReset = this.container.querySelector("#btn-reset-view");
    btnReset?.addEventListener("click", () => {
      this.selectedHotspotId = null;
      const arrowLayer = this.container?.querySelector("#callout-arrow-layer");
      if (arrowLayer) arrowLayer.innerHTML = "";
      this.updateHotspotVisuals("");
      const title = this.container?.querySelector("#active-hotspot-title");
      if (title) title.textContent = "Visão Geral Panorâmica (Toque em um ponto)";
      const torque = this.container?.querySelector("#active-hotspot-torque");
      if (torque) torque.textContent = "";
    });
  }
}
